import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@services/api.js';

/* Every mounted useProgress() is its own copy of the data, fetched once. Code
   that has just changed a user's progress (a rep pass that was saved) calls
   refreshProgress() and each mounted copy re-reads. The re-read is silent — it
   does not flip `loading`, so pages do not drop back to their skeleton. */
const refreshListeners = new Set();
export const refreshProgress = () => { refreshListeners.forEach(fn => fn()); };

export const useProgress = () => {
  const { user, loading: authLoading } = useAuth();
  const [completedExercises, setCompleted] = useState([]);
  const [fetching, setFetching]           = useState(false);
  const [error, setError]                 = useState(null);
  /* `settled` = we now know what there is to know: either the fetch has
     come back, or auth has resolved to "nobody is signed in". It starts
     FALSE, which is the whole point.

     `loading` used to be useState(false) and was only flipped true
     inside the effect below — i.e. after the first render had already
     committed. Every consumer gates its loading screen on this flag, so
     the sequence was: paint the whole page -> effect runs -> show the
     loader -> fetch resolves -> paint the page again. That is the
     "page flashes, then the loader appears" report, and it was the
     hook's doing rather than any page's.

     Deriving `loading` from authLoading too (rather than just flipping
     the initial value to true) matters: `user` is undefined while auth
     is still resolving, which would take the !user branch below, settle,
     and drop the loader — then auth resolves, the fetch starts, and the
     loader comes BACK. A second loading screen after the first. Waiting
     on auth first collapses that into one continuous load. */
  const [settled, setSettled] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Auth hasn't resolved yet: nothing is knowable, stay unsettled.
    if (authLoading) return;

    // Reset when user changes / signs out. Nothing to fetch, so this is
    // as settled as it gets.
    if (!user) {
      setCompleted([]);
      setFetching(false);
      setSettled(true);
      fetchedRef.current = false;
      return;
    }

    // Don't fetch more than once per session
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const load = async () => {
      setFetching(true);
      try {
        const { data, error } = await supabase
          .from('user_progress')
          .select('*')
          .eq('user_id', user.id);

        if (error) throw error;
        setCompleted(data ?? []);
      } catch (err) {
        console.error('Failed to load progress:', err.message);
        setError(err.message);
      } finally {
        setFetching(false);
        setSettled(true);
      }
    };

    load();
  }, [user?.id, authLoading]);

  const userId = user?.id;
  useEffect(() => {
    if (!userId) return undefined;
    const reload = async () => {
      const { data, error } = await supabase.from('user_progress').select('*').eq('user_id', userId);
      if (error) console.warn('Failed to refresh progress:', error.message);
      else setCompleted(data ?? []);
    };
    refreshListeners.add(reload);
    return () => { refreshListeners.delete(reload); };
  }, [userId]);

  /* Same name and same meaning as before ("is this hook still working?"),
     so all seven existing consumers keep working unchanged. The only
     behavioural difference is that it is now true on the FIRST render
     instead of becoming true one render late. */
  const loading = authLoading || !settled || fetching;

  const isCompleted = useCallback((lessonId, exerciseOrder) =>
    completedExercises.some(e => e.lesson_id === lessonId && e.exercise_order === exerciseOrder),
  [completedExercises]);

  const getScore = useCallback((lessonId, exerciseOrder) => {
    const entry = completedExercises.find(e => e.lesson_id === lessonId && e.exercise_order === exerciseOrder);
    return entry?.score ?? null;
  }, [completedExercises]);

  const getLessonProgress = useCallback((lessonId) =>
    completedExercises.filter(e => e.lesson_id === lessonId).length,
  [completedExercises]);

  const scores = completedExercises.map(e => e.score).filter(Boolean);
  const averageScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

  return {
    completedExercises,
    loading,
    error,
    isCompleted,
    getScore,
    getLessonProgress,
    averageScore,
    totalCompleted: completedExercises.length,
  };
};

export default useProgress;
